import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { MeUserProjection } from '@kennisbank/shared';

/**
 * Cookie-session auth. The browser holds an httpOnly signed session cookie
 * (`kennisbank.sid`) that the backend manages. We never see or touch it
 * from JavaScript — every API request just uses `credentials: 'include'`
 * to attach the cookie automatically.
 *
 * In development a Bearer dev-token is still supported via the persona
 * picker on /login. We stash it in sessionStorage and append it as
 * Authorization header. The backend's authenticate middleware short-circuits
 * the bearer for known dev tokens; production rejects bearers entirely.
 */
const DEV_TOKEN_STORAGE_KEY = 'kennisbank.devToken';

/**
 * Server-side feature flags surfaced on /api/me (issue #3 et al). Components
 * gate UI on these instead of asking the backend per-feature. New flags
 * land here as the backend grows them in `routes/me.ts`.
 */
export interface AuthFeatureFlags {
  canvasEnabled: boolean;
  /** My Agent phased rollout (MY_AGENT_ENABLED + OID allowlist, server-side). */
  myAgentEnabled: boolean;
}

export interface AuthContextValue {
  user: MeUserProjection | null;
  flags: AuthFeatureFlags;
  loading: boolean;
  source: 'cookie' | 'dev_token' | null;
  /** Set a dev token (or clear). Triggers /api/me re-fetch. */
  setDevToken: (token: string | null) => void;
  /** Sign out. POSTs to /api/v1/oauth/entra/logout, clears dev token, navigates to /login. */
  signOut: () => Promise<void>;
}

const DEFAULT_FLAGS: AuthFeatureFlags = { canvasEnabled: false, myAgentEnabled: false };

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [devToken, setDevTokenState] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : window.sessionStorage.getItem(DEV_TOKEN_STORAGE_KEY),
  );
  const [user, setUser] = useState<MeUserProjection | null>(null);
  const [flags, setFlags] = useState<AuthFeatureFlags>(DEFAULT_FLAGS);
  // Always start in loading=true so RequireAuth can wait for the first
  // /api/me round-trip before deciding to bounce to /login. Cookie-based
  // auth is opaque from the client — we only know we're signed in by
  // asking the backend.
  const [loading, setLoading] = useState<boolean>(true);

  const setDevToken = useCallback((token: string | null) => {
    if (token) {
      window.sessionStorage.setItem(DEV_TOKEN_STORAGE_KEY, token);
    } else {
      window.sessionStorage.removeItem(DEV_TOKEN_STORAGE_KEY);
    }
    setDevTokenState(token);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch('/api/v1/oauth/entra/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout request failed (cookie may persist server-side until expiry)', err);
    }
    setDevToken(null);
    setUser(null);
    // Hard navigation guarantees no stale React state survives the sign-out.
    window.location.assign('/login');
  }, [setDevToken]);

  // Resolve user via /api/me on mount + whenever devToken changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const headers: Record<string, string> = {};
        if (devToken) headers.Authorization = `Bearer ${devToken}`;
        const res = await fetch('/api/me', {
          credentials: 'include',
          headers,
        });
        if (cancelled) return;
        if (res.status === 401) {
          // Either no session cookie (normal pre-login) or dev token rejected.
          if (devToken) setDevToken(null);
          setUser(null);
          setFlags(DEFAULT_FLAGS);
          return;
        }
        if (!res.ok) {
          console.error('Failed to load /api/me', res.status);
          setUser(null);
          setFlags(DEFAULT_FLAGS);
          return;
        }
        const json = (await res.json()) as MeUserProjection & {
          flags?: Partial<AuthFeatureFlags>;
        };
        // Strip server-side `flags` off the user projection before storing,
        // so consumers reading `user` keep the same shape they always had.
        const { flags: incomingFlags, ...userOnly } = json;
        setUser(userOnly as MeUserProjection);
        setFlags({
          canvasEnabled: incomingFlags?.canvasEnabled === true,
          myAgentEnabled: incomingFlags?.myAgentEnabled === true,
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Network error loading /api/me', err);
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [devToken, setDevToken]);

  const source: AuthContextValue['source'] = user ? (devToken ? 'dev_token' : 'cookie') : null;

  const value = useMemo<AuthContextValue>(
    () => ({ user, flags, loading, source, setDevToken, signOut }),
    [user, flags, loading, source, setDevToken, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
